// src/pages/ProfilePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { authService } from '../services/authService';

const ProfilePage = () => {
  const navigate = useNavigate();
  const { user, logout, isLoggedIn, login } = useUser();
  const [profile, setProfile] = useState({ id: null, name: '', email: '', phoneNo: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // Password Change States
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [pwdData, setPwdData] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [showOldPwd, setShowOldPwd] = useState(false);
  const [showNewPwd, setShowNewPwd] = useState(false);
  const [showConfPwd, setShowConfPwd] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [pwdError, setPwdError] = useState('');
  const [pwdSuccess, setPwdSuccess] = useState('');

  useEffect(() => {
    if (!isLoggedIn()) {
      navigate('/login', { replace: true });
      return;
    }
    
    const fetchProfile = async () => {
      try {
        const data = await authService.getProfile();
        setProfile({
          id: data.id, 
          name: data.name || '',
          email: data.email || '',
          phoneNo: data.phoneNo || ''
        });
        setError(null);
        if (data.id && user && user.id !== data.id) {
          login({ ...user, id: data.id, name: data.name || user.name });
        }
      } catch (err) {
        // FIXED: Broadened error check to catch 500s or CORS errors caused by expired tokens
        const status = err.response?.status;
        if (status === 401 || status === 403 || status === 500 || !err.response) {
          localStorage.removeItem('svmart_user'); // Forcefully clear storage
          logout();
          navigate('/login', { replace: true });
          return;
        }
        setError("Failed to load profile details.");
        if (user) {
          setProfile({ id: user.id, name: user.name || '', email: user.email || '', phoneNo: '' });
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchProfile();
  }, [isLoggedIn, navigate, user, login, logout]);

  const handleChange = (e) => {
    setProfile({ ...profile, [e.target.name]: e.target.value });
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      await authService.updateDetails({ name: profile.name, phoneNo: profile.phoneNo });
      setIsEditing(false);
      login({ ...user, name: profile.name });
      alert("Profile updated successfully!");
    } catch (err) {
      const status = err.response?.status;
      if (status === 401 || status === 403 || status === 500 || !err.response) {
        localStorage.removeItem('svmart_user');
        logout();
        navigate('/login', { replace: true });
        return;
      }
      alert("Failed to update profile. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handlePwdChange = (e) => {
    setPwdData({ ...pwdData, [e.target.name]: e.target.value });
    setPwdError('');
    setPwdSuccess('');
  };

  const submitChangePassword = async () => {
    if (!pwdData.oldPassword || !pwdData.newPassword || !pwdData.confirmPassword) {
      setPwdError('All fields are required.');
      return;
    }
    if (pwdData.newPassword !== pwdData.confirmPassword) {
      setPwdError('New passwords do not match.');
      return;
    }
    if (pwdData.newPassword.length < 6) {
      setPwdError('New password must be at least 6 characters long.');
      return;
    }

    setPwdSaving(true);
    try {
      const res = await authService.changePassword({
        oldPassword: pwdData.oldPassword,
        newPassword: pwdData.newPassword
      });
      setPwdSuccess(typeof res === 'string' ? res : 'Password changed successfully!');
      setPwdData({ oldPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => {
        setShowPasswordForm(false);
        setPwdSuccess('');
      }, 2500);
    } catch (err) {
      setPwdError(err.response?.data?.message || err.response?.data || 'Failed to change password. Please check your old password.');
    } finally {
      setPwdSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (window.confirm("Are you sure you want to completely delete your account? This action cannot be undone.")) {
      try {
        const targetId = profile.id || user.id;
        await authService.deleteAccount(targetId);
        alert("Account deleted successfully.");
        localStorage.removeItem('svmart_user');
        logout();
        navigate('/quick', { replace: true });
      } catch (err) {
        const status = err.response?.status;
        if (status === 401 || status === 403 || status === 500 || !err.response) {
          localStorage.removeItem('svmart_user');
          logout();
          navigate('/login', { replace: true });
          return;
        }
        alert("Failed to delete account. Please try again.");
      }
    }
  };

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      localStorage.removeItem('svmart_user'); // Explicitly clear storage on regular logout
      logout();
      navigate('/quick', { replace: true });
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 min-h-[500px]">
        <i className="fas fa-spinner fa-spin text-4xl text-[#0F2F63] mb-4"></i>
        <p className="text-gray-500 font-medium">Loading Profile...</p>
      </div>
    );
  }

  return (
    <div className="py-8 px-4 max-w-3xl mx-auto w-full min-h-[70vh]">
      <div className="bg-white rounded-3xl shadow-sm border border-gray-100 overflow-hidden">
        
        {/* Profile Header */}
        <div className="bg-[#0F2F63] p-8 text-white flex items-center gap-6">
          <div className="w-24 h-24 rounded-full bg-white/20 flex items-center justify-center text-4xl font-bold backdrop-blur-sm border-2 border-white/30">
            {profile.name ? profile.name.charAt(0).toUpperCase() : 'U'}
          </div>
          <div>
            <h1 className="text-3xl font-black">{profile.name || 'User'}</h1>
            <p className="text-white/80 mt-1 flex items-center gap-2">
              <i className="fas fa-envelope"></i> {profile.email}
            </p>
          </div>
        </div>

        <div className="p-8">
          {error && <div className="text-red-500 mb-4 font-medium text-sm"><i className="fas fa-exclamation-triangle"></i> {error}</div>}
          
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900">Personal Information</h2>
            {!isEditing && (
              <button onClick={() => setIsEditing(true)} className="text-sky-600 font-bold text-sm flex items-center gap-1 hover:text-sky-700">
                <i className="fas fa-edit"></i> Edit Profile
              </button>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-500 mb-2">Full Name</label>
              <input
                type="text"
                name="name"
                value={profile.name}
                onChange={handleChange}
                disabled={!isEditing}
                className={`w-full px-4 py-3 rounded-xl border ${isEditing ? 'border-sky-300 bg-white focus:outline-none focus:ring-2 focus:ring-sky-100' : 'border-gray-100 bg-gray-50 text-gray-600'} transition-all`}
              />
            </div>
            
            <div>
              <label className="block text-sm font-bold text-gray-500 mb-2">Email Address</label>
              <input
                type="email"
                name="email"
                value={profile.email}
                disabled={true}
                className="w-full px-4 py-3 rounded-xl border border-gray-100 bg-gray-50 text-gray-400 cursor-not-allowed"
                title="Email cannot be changed"
              />
            </div>

            <div>
              <label className="block text-sm font-bold text-gray-500 mb-2">Phone Number</label>
              <input
                type="tel"
                name="phoneNo"
                value={profile.phoneNo}
                onChange={handleChange}
                disabled={!isEditing}
                placeholder="Add your phone number"
                className={`w-full px-4 py-3 rounded-xl border ${isEditing ? 'border-sky-300 bg-white focus:outline-none focus:ring-2 focus:ring-sky-100' : 'border-gray-100 bg-gray-50 text-gray-600'} transition-all`}
              />
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-4 mt-8 pt-6 border-t border-gray-100">
              <button 
                onClick={handleUpdate} 
                disabled={saving}
                className="bg-[#2CCE75] hover:bg-[#1EA85E] text-white px-8 py-3 rounded-xl font-bold flex items-center gap-2 transition-colors disabled:opacity-70"
              >
                {saving ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-save"></i>}
                Save Changes
              </button>
              <button 
                onClick={() => setIsEditing(false)} 
                className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-8 py-3 rounded-xl font-bold transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          {/* Quick Links / My Account */}
          <div className="mt-12 pt-8 border-t border-gray-100 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">My Account</h3>
            
            <button 
              onClick={() => navigate('/orders')}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors"
            >
              <span className="flex items-center gap-3"><i className="fas fa-shopping-bag text-sky-500"></i> My Orders</span>
              <i className="fas fa-chevron-right text-gray-300 text-sm"></i>
            </button>

            <button 
              onClick={() => navigate('/addresses')}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors"
            >
              <span className="flex items-center gap-3"><i className="fas fa-map-marker-alt text-[#2CCE75]"></i> Saved Addresses</span>
              <i className="fas fa-chevron-right text-gray-300 text-sm"></i>
            </button>
          </div>

          {/* Security Settings */}
          <div className="mt-8 pt-8 border-t border-gray-100 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Security</h3>
            
            {!showPasswordForm ? (
              <button 
                onClick={() => setShowPasswordForm(true)}
                className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors"
              >
                <span className="flex items-center gap-3"><i className="fas fa-key text-amber-500"></i> Change Password</span>
                <i className="fas fa-chevron-right text-gray-300 text-sm"></i>
              </button>
            ) : (
              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-200 animate-fade-in space-y-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-bold text-gray-800">Change Password</h4>
                  <button onClick={() => { setShowPasswordForm(false); setPwdError(''); setPwdSuccess(''); }} className="text-gray-400 hover:text-red-500 transition-colors">
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                
                {pwdError && <div className="text-red-500 text-sm font-bold flex items-center gap-2"><i className="fas fa-exclamation-circle"></i> {pwdError}</div>}
                {pwdSuccess && <div className="text-emerald-500 text-sm font-bold flex items-center gap-2"><i className="fas fa-check-circle"></i> {pwdSuccess}</div>}

                <div className="space-y-4">
                  <div className="relative">
                    <input
                      type={showOldPwd ? "text" : "password"}
                      name="oldPassword"
                      value={pwdData.oldPassword}
                      onChange={handlePwdChange}
                      placeholder="Old Password"
                      className="w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all pr-12 text-gray-900 font-semibold"
                    />
                    <button type="button" onClick={() => setShowOldPwd(!showOldPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                      <i className={`fas ${showOldPwd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>
                  
                  <div className="relative">
                    <input
                      type={showNewPwd ? "text" : "password"}
                      name="newPassword"
                      value={pwdData.newPassword}
                      onChange={handlePwdChange}
                      placeholder="New Password"
                      className="w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all pr-12 text-gray-900 font-semibold"
                    />
                    <button type="button" onClick={() => setShowNewPwd(!showNewPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                      <i className={`fas ${showNewPwd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>

                  <div className="relative">
                    <input
                      type={showConfPwd ? "text" : "password"}
                      name="confirmPassword"
                      value={pwdData.confirmPassword}
                      onChange={handlePwdChange}
                      placeholder="Confirm New Password"
                      className="w-full px-4 py-3 rounded-xl border border-sky-200 bg-white focus:outline-none focus:ring-2 focus:ring-sky-100 transition-all pr-12 text-gray-900 font-semibold"
                    />
                    <button type="button" onClick={() => setShowConfPwd(!showConfPwd)} className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-1">
                      <i className={`fas ${showConfPwd ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                    </button>
                  </div>

                  <button
                    onClick={submitChangePassword}
                    disabled={pwdSaving}
                    className="w-full bg-[#0F2F63] hover:bg-[#1a4a8a] text-white px-6 py-3.5 rounded-xl font-black transition-all disabled:opacity-70 flex items-center justify-center gap-2 shadow-md"
                  >
                    {pwdSaving ? <i className="fas fa-spinner fa-spin"></i> : 'Update Password'}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Account Actions */}
          <div className="mt-8 pt-8 border-t border-gray-100 space-y-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Account Actions</h3>
            
            <button 
              onClick={handleLogout}
              className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 rounded-xl text-gray-700 font-bold transition-colors"
            >
              <span className="flex items-center gap-3"><i className="fas fa-sign-out-alt text-gray-400"></i> Logout</span>
              <i className="fas fa-chevron-right text-gray-300 text-sm"></i>
            </button>
            
            <button 
              onClick={handleDeleteAccount}
              className="w-full flex items-center justify-between px-6 py-4 bg-red-50 hover:bg-red-100 rounded-xl text-red-600 font-bold transition-colors"
            >
              <span className="flex items-center gap-3"><i className="fas fa-trash-alt text-red-400"></i> Delete Account</span>
              <i className="fas fa-chevron-right text-red-300 text-sm"></i>
            </button>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;